import { useTranslation } from 'react-i18next';
import type { RecordedReason } from '../api/types';
import { journalDescriptionWith, systemReasonWith, type ReasonLookup, type ReasonText } from '../lib/systemReason';
import { TechnicalDetail } from './States';

function useLookup(): ReasonLookup {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  return (key) => (i18n.exists(key) ? tr(key) : undefined);
}

/** A recorded value in words, else as it is ('' when there is none). */
const asText = (value: RecordedReason, words: (text: string) => ReasonText | null): ReasonText =>
  typeof value === 'string' ? (words(value) ?? { text: value, detail: null }) : { text: '', detail: null };

/** A recorded reason in words: one the platform writes in the operator's language, a person's as typed, none as ''. */
export function useSystemReason() {
  const lookup = useLookup();
  return (reason: RecordedReason): ReasonText => asText(reason, (r) => systemReasonWith(r, lookup));
}

/** A journal entry's description in words, by the entry's type when the page has it (a wallet adjustment's: the staff member's text). */
export function useJournalDescription() {
  const lookup = useLookup();
  return (description: RecordedReason, entryType?: string | null): ReasonText => asText(description, (d) => journalDescriptionWith(d, entryType, lookup));
}

/** A recorded reason: one the platform writes in the operator's language (original as the tooltip, a registry's own text folded away), a person's text as typed. */
export function SystemReason({ reason }: { reason: RecordedReason }) {
  const { t } = useTranslation();
  const r = useSystemReason()(reason);
  if (typeof reason !== 'string' || !reason) return null;
  if (r.text === reason) return <>{reason}</>;
  const text = <span title={t('systemReason.original', { text: reason })}>{r.text}</span>;
  return r.detail ? (
    <span className="grid gap-0.5">
      {text}
      <TechnicalDetail detail={r.detail} />
    </span>
  ) : (
    text
  );
}
